import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHero from "../components/PageHero";
import ProjectCard from "../components/ProjectsGallery/ProjectCard";
import ProjectLightbox from "../components/ProjectsGallery/ProjectLightbox";
import WorksList from "../components/ProjectsGallery/WorksList";
import { projects, categories } from "../components/ProjectsGallery/projects";

const ALL = "All";
const PAGE_SIZE = 9;

function ProjectsGalleryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // True when the lightbox was opened from this page (so Back can close it).
  const openedHere = useRef(false);

  const requested = searchParams.get("category");
  const activeCategory = categories.includes(requested) ? requested : ALL;
  const activeProject = projects.find((p) => p.slug === searchParams.get("project")) || null;

  const visible = useMemo(
    () => (activeCategory === ALL ? projects : projects.filter((p) => p.projectCategory === activeCategory)),
    [activeCategory]
  );

  // Show projects in batches so the page stays a reasonable length on phones.
  const [shown, setShown] = useState(PAGE_SIZE);
  useEffect(() => setShown(PAGE_SIZE), [activeCategory]);

  const counts = useMemo(() => {
    const c = { [ALL]: projects.length };
    projects.forEach((p) => (c[p.projectCategory] = (c[p.projectCategory] || 0) + 1));
    return c;
  }, []);

  const updateParams = useCallback(
    (changes, options) => {
      setSearchParams((prev) => {
        const nextParams = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) =>
          value ? nextParams.set(key, value) : nextParams.delete(key)
        );
        return nextParams;
      }, options);
    },
    [setSearchParams]
  );

  const selectCategory = (category) =>
    updateParams({ category: category === ALL ? null : category }, { replace: true });

  const openProject = (project) => {
    openedHere.current = true;
    updateParams({ project: project.slug });
  };

  const closeProject = () => {
    if (openedHere.current) {
      openedHere.current = false;
      navigate(-1);
    } else {
      updateParams({ project: null }, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero
        title="Our Projects"
        crumb="Projects"
        subtitle="Explore our portfolio of successful construction projects across buildings, bridges, roads, and infrastructure"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Category filter */}
        <div
          role="group"
          aria-label="Filter projects by category"
          className="mb-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
        >
          {[ALL, ...categories].map((category) => {
            const active = category === activeCategory;
            return (
              <button
                key={category}
                type="button"
                onClick={() => selectCategory(category)}
                aria-pressed={active}
                className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-2 text-sm font-semibold transition-all duration-200 ${
                  active
                    ? "border-brand-navy bg-brand-navy text-white shadow-md"
                    : "border-gray-200 bg-white text-gray-700 hover:border-brand-yellow hover:text-brand-navy"
                }`}
              >
                {category}
                <span
                  className={`rounded-full px-2 py-0.5 text-xs ${
                    active ? "bg-brand-yellow text-brand-navy" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {counts[category]}
                </span>
              </button>
            );
          })}
        </div>

        <p className="sr-only" aria-live="polite">
          Showing {visible.length} {visible.length === 1 ? "project" : "projects"}
          {activeCategory !== ALL ? ` in ${activeCategory}` : ""}
        </p>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.slice(0, shown).map((project, index) => (
            <ProjectCard key={project.slug} project={project} index={index} onOpen={openProject} />
          ))}
        </ul>

        {visible.length > shown && (
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => setShown((n) => n + PAGE_SIZE)}
              className="inline-flex items-center gap-2 rounded-full border-2 border-brand-navy px-7 py-3 font-bold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
            >
              Show more projects
              <span className="text-sm font-semibold opacity-70">
                ({shown} of {visible.length})
              </span>
            </button>
          </div>
        )}
      </section>

      <WorksList />

      {activeProject && <ProjectLightbox project={activeProject} onClose={closeProject} />}
    </div>
  );
}

export default ProjectsGalleryPage;
